import {
  Avatar,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link as ChakraLink,
  Spinner,
  Table,
  Tbody,
  Td,
  Badge,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState } from 'react';
import { Clock, Mail, Phone, User, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { djs } from '@duke-hq/djs';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { formatCourseLabel as courseLabel } from '@duke-hq/lib/course';
import { getUserFullName } from '@duke-hq/lib/user';
import {
  SupportTaskKind,
  SupportTaskProcessor,
  SupportTaskStatus,
} from '@duke-hq/svc-api-db/client';
import { useAuthAdmin } from '../AuthProviderAdmin';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { isResourceStatus } from '@duke-hq/shared-constants/resourceStatuses';
import SupersededCourseStatusBadge from '@duke-hq/enrolments-ui/components/SupersededCourseStatusBadge';
import { formatTaskStatus, formatTaskType, getTaskStatusDisplay } from './utils/formatters';
import { SYDNEY_TZ } from './utils/constants';
import { getTaskStatusDisplay as getTaskSchedulingStatus } from './utils/taskStatus';
import { ApproveReenrolmentModal } from './ApproveReenrolmentModal';
import { RejectReenrolmentModal } from './RejectReenrolmentModal';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { TransferSupersededEnrolmentModal } from './TransferSupersededEnrolmentModal';

interface SupportTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId?: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

export function SupportTaskModal({
  isOpen,
  onClose,
  taskId,
  onComplete,
  onCancel,
}: SupportTaskModalProps) {
  const utils = trpcAdmin.useUtils();
  const session = useAuthAdmin();
  const hasManagePermission = useCheckPermission('canManageSupportTasks');

  const { data: task, isLoading } = trpcAdmin.support.task.get.useQuery(
    { taskId: taskId ?? '' },
    { enabled: taskId !== undefined && taskId !== '' },
  );

  const assignToMeMutation = trpcAdmin.support.task.assign.useMutation({
    onSuccess: () => {
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
    },
  });

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const currentUserDrn = session.user?.drn;

  const handleAssignToMe = () => {
    if (taskId !== undefined && taskId !== '' && currentUserDrn !== undefined) {
      assignToMeMutation.mutate({
        taskId,
        assignedToDrn: currentUserDrn,
      });
    }
  };

  if (!isOpen || taskId === undefined || taskId === '') {
    return null;
  }

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody>
            <Flex justify="center" align="center" h="full">
              <Spinner size="xl" />
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  if (!task) {
    return null;
  }

  const statusDisplay = getTaskStatusDisplay(task.status);
  const studentName =
    task.student != null
      ? (() => {
          const name = getUserFullName(task.student);
          return name != null && name !== '' ? name : t`Unknown Student`;
        })()
      : t`Unknown Student`;
  const studentId = task.student?.id ?? null;
  const canAssignToSelf =
    (task.assignedToDrn == null || task.assignedToDrn === '') &&
    task.processor === SupportTaskProcessor.HUMAN;
  const canComplete =
    task.processor === SupportTaskProcessor.HUMAN &&
    (task.status === SupportTaskStatus.PENDING || task.status === SupportTaskStatus.IN_PROGRESS);
  const canCancelTask =
    hasManagePermission &&
    (task.status === SupportTaskStatus.PENDING || task.status === SupportTaskStatus.IN_PROGRESS);

  const isReenrolmentReview =
    task.kind === SupportTaskKind.REENROLMENT_REVIEW && task.reenrolmentDetails != null;
  const canResolveReenrolment = isReenrolmentReview && canComplete;

  const isSupersededTransfer =
    task.kind === SupportTaskKind.SUPERSEDED_COURSE_TRANSFER && task.supersessionDetails != null;
  const canTransferSuperseded = isSupersededTransfer && canComplete;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" py="3">
          <HStack justify="space-between" align="center">
            <HStack align="center">
              <Text fontSize="xl" fontWeight="semibold">
                {formatTaskType(task.kind, task.title)}
              </Text>
              <Badge
                fontSize="xs"
                fontWeight="bold"
                borderRadius="full"
                colorScheme={statusDisplay.colorScheme}
                variant={statusDisplay.variant}
                textTransform="none"
              >
                {formatTaskStatus(task.status)}
              </Badge>
            </HStack>
            <IconButton
              icon={<X size={iconSize.lg} />}
              aria-label={t`Close`}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={6} py={4}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
                <Trans>Task Information</Trans>
              </Text>
              <VStack align="stretch" spacing={2}>
                {(() => {
                  const schedulingStatus = getTaskSchedulingStatus({
                    status: task.status,
                    processor: task.processor,
                    kind: task.kind,
                    processAnytimeAfter: task.processAnytimeAfter,
                    completedAt: task.completedAt,
                    enrolmentCreatedAt: task.enrolment?.created_at,
                  });

                  return (
                    <HStack>
                      <Icon
                        as={schedulingStatus.icon}
                        boxSize={4}
                        color={schedulingStatus.color ?? 'fg.muted'}
                      />
                      <Text fontSize="sm" color="fg.muted">
                        {schedulingStatus.label}
                      </Text>
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={
                            schedulingStatus.color != null
                              ? `${schedulingStatus.color.split('.')[0]}.600`
                              : undefined
                          }
                        >
                          {schedulingStatus.text}
                        </Text>
                        {schedulingStatus.showDayNumber && schedulingStatus.dayNumber != null ? (
                          <Text fontSize="xs" color="fg.muted">
                            <Trans>Day {schedulingStatus.dayNumber} of enrolment</Trans>
                          </Text>
                        ) : null}
                      </VStack>
                    </HStack>
                  );
                })()}
                <HStack>
                  <Icon as={Clock} boxSize={4} color="fg.muted" />
                  <Text fontSize="sm" color="fg.muted">
                    <Trans>Created:</Trans>
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {djs(task.createdAt).tz(SYDNEY_TZ).format('DD MMM YYYY, h:mm A')}
                  </Text>
                </HStack>
                {task.assignedTo ? (
                  <HStack>
                    <Icon as={User} boxSize={4} color="fg.muted" />
                    <Text fontSize="sm" color="fg.muted">
                      <Trans>Assigned to:</Trans>
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {getUserFullName(task.assignedTo)}
                    </Text>
                  </HStack>
                ) : null}
                {task.completedAt && task.completedBy ? (
                  <>
                    <HStack>
                      <Icon as={Clock} boxSize={4} color="fg.muted" />
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Completed:</Trans>
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {djs(task.completedAt).tz(SYDNEY_TZ).format('DD MMM YYYY, h:mm A')}
                      </Text>
                    </HStack>
                    <HStack>
                      <Icon as={User} boxSize={4} color="fg.muted" />
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Completed by:</Trans>
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {getUserFullName(task.completedBy)}
                      </Text>
                    </HStack>
                  </>
                ) : null}
              </VStack>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
                <Trans>Student Information</Trans>
              </Text>
              <HStack align="start" spacing={3}>
                <Avatar size="md" name={studentName} />
                <VStack align="start" spacing={1}>
                  {studentId != null && studentId !== '' ? (
                    <Button
                      as={Link}
                      to={`/student/${studentId}`}
                      variant="link"
                      size="sm"
                      fontWeight="semibold"
                    >
                      {studentName}
                    </Button>
                  ) : (
                    <Text fontSize="sm" fontWeight="semibold">
                      {studentName}
                    </Text>
                  )}
                  {task.student?.email != null && task.student.email !== '' ? (
                    <HStack spacing={1}>
                      <Icon as={Mail} boxSize={4} color="fg.muted" />
                      <Text fontSize="sm" color="fg.muted">
                        {task.student.email}
                      </Text>
                    </HStack>
                  ) : null}
                  {task.student?.phone_number != null && task.student.phone_number !== '' ? (
                    <HStack spacing={1}>
                      <Icon as={Phone} boxSize={4} color="fg.muted" />
                      <Text fontSize="sm" color="fg.muted">
                        {task.student.phone_number}
                      </Text>
                    </HStack>
                  ) : null}
                </VStack>
              </HStack>
            </Box>

            {task.creditTransferDetails != null ? (
              <CreditTransferDetailsTable
                details={task.creditTransferDetails}
                enrolment={
                  task.enrolment != null
                    ? {
                        id: task.enrolment.id,
                        courseIdentifier: task.enrolment.course?.identifier ?? null,
                      }
                    : null
                }
                variant={
                  task.kind === SupportTaskKind.CREDIT_TRANSFER_FOLLOW_UP ? 'followUp' : 'review'
                }
              />
            ) : null}

            {task.kind === SupportTaskKind.REENROLMENT_REVIEW && task.reenrolmentDetails != null ? (
              <ReenrolmentComparisonTable
                current={task.reenrolmentDetails.currentEnrolment}
                proposed={task.reenrolmentDetails.proposedEnrolment}
              />
            ) : isSupersededTransfer ? (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
                  <Trans>Supersession Details</Trans>
                </Text>
                <VStack align="stretch" spacing={2}>
                  {task.enrolment?.course != null ? (
                    <HStack align="start">
                      <Text fontSize="sm" color="fg.muted" minW="140px" flexShrink={0}>
                        <Trans>Current course:</Trans>
                      </Text>
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                          <SupersededCourseStatusBadge
                            status="SUPERSEDED"
                            supersedingCourse={task.supersessionDetails?.supersedingCourse}
                          />
                          <Text fontSize="sm" fontWeight="medium">
                            {task.enrolment.course.title}
                          </Text>
                        </HStack>
                        <HStack spacing={2} align="center">
                          {task.enrolment.course.identifier != null &&
                          task.enrolment.course.identifier !== '' ? (
                            <Text fontSize="xs" color="fg.muted">
                              {task.enrolment.course.identifier}
                            </Text>
                          ) : null}
                          <Button
                            as={Link}
                            to={`/enrolment/${task.enrolment.id}`}
                            variant="link"
                            size="sm"
                          >
                            <Trans>View enrolment</Trans>
                          </Button>
                        </HStack>
                      </VStack>
                    </HStack>
                  ) : null}
                  {task.supersessionDetails?.supersedingCourse != null ? (
                    <HStack align="start">
                      <Text fontSize="sm" color="fg.muted" minW="140px" flexShrink={0}>
                        <Trans>Superseding course:</Trans>
                      </Text>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {task.supersessionDetails.supersedingCourse.title}
                        </Text>
                        {task.supersessionDetails.supersedingCourse.identifier != null &&
                        task.supersessionDetails.supersedingCourse.identifier !== '' ? (
                          <HStack spacing={2} align="center">
                            <Text fontSize="xs" color="fg.muted">
                              {task.supersessionDetails.supersedingCourse.identifier}
                            </Text>
                            {task.status === SupportTaskStatus.COMPLETED &&
                            task.supersessionDetails.newEnrolmentId != null ? (
                              <Button
                                as={Link}
                                to={`/enrolment/${task.supersessionDetails.newEnrolmentId}`}
                                variant="link"
                                size="sm"
                              >
                                <Trans>View enrolment</Trans>
                              </Button>
                            ) : null}
                          </HStack>
                        ) : task.status === SupportTaskStatus.COMPLETED &&
                          task.supersessionDetails.newEnrolmentId != null ? (
                          <Button
                            as={Link}
                            to={`/enrolment/${task.supersessionDetails.newEnrolmentId}`}
                            variant="outline"
                            size="xs"
                          >
                            <Trans>View enrolment</Trans>
                          </Button>
                        ) : null}
                      </VStack>
                    </HStack>
                  ) : null}
                  {task.provider ? (
                    <HStack>
                      <Text fontSize="sm" color="fg.muted" minW="140px" flexShrink={0}>
                        <Trans>Provider:</Trans>
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {task.provider.name}
                      </Text>
                    </HStack>
                  ) : null}
                </VStack>
              </Box>
            ) : task.enrolment != null && task.creditTransferDetails == null ? (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
                  <Trans>Enrolment Information</Trans>
                </Text>
                <VStack align="stretch" spacing={2}>
                  {task.enrolment.course != null ? (
                    <HStack align="start">
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Course:</Trans>
                      </Text>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {task.enrolment.course.title}
                        </Text>
                        {task.enrolment.course.identifier != null &&
                        task.enrolment.course.identifier !== '' ? (
                          <Text fontSize="xs" color="fg.muted">
                            {task.enrolment.course.identifier}
                          </Text>
                        ) : null}
                      </VStack>
                    </HStack>
                  ) : null}
                  {task.enrolment.enrolment_contract_type ? (
                    <HStack>
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Contract Type:</Trans>
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {task.enrolment.enrolment_contract_type}
                      </Text>
                    </HStack>
                  ) : null}
                  {task.provider ? (
                    <HStack>
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Provider:</Trans>
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {task.provider.name}
                      </Text>
                    </HStack>
                  ) : null}
                </VStack>
              </Box>
            ) : null}

            <HStack spacing={2}>
              {canAssignToSelf && !canResolveReenrolment && !canTransferSuperseded ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAssignToMe}
                  isLoading={assignToMeMutation.isPending}
                >
                  <Trans>Assign to me</Trans>
                </Button>
              ) : null}
              {canResolveReenrolment ? (
                <>
                  <Button size="sm" colorScheme="green" onClick={() => setIsApproveModalOpen(true)}>
                    <Trans>Approve Re-enrolment</Trans>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => setIsRejectModalOpen(true)}
                  >
                    <Trans>Reject Re-enrolment</Trans>
                  </Button>
                </>
              ) : canTransferSuperseded ? (
                <Button size="sm" colorScheme="blue" onClick={() => setIsTransferModalOpen(true)}>
                  <Trans>Transfer to Superseding Course</Trans>
                </Button>
              ) : canComplete && onComplete ? (
                <Button size="sm" colorScheme="blue" onClick={onComplete}>
                  {task.kind === SupportTaskKind.CREDIT_TRANSFER_REVIEW ? (
                    <Trans>Mark as reviewed</Trans>
                  ) : task.kind === SupportTaskKind.CREDIT_TRANSFER_FOLLOW_UP ? (
                    <Trans>Contacted student</Trans>
                  ) : (
                    <Trans>Complete Task</Trans>
                  )}
                </Button>
              ) : null}
              {canCancelTask && task.kind !== SupportTaskKind.CREDIT_TRANSFER_REVIEW && onCancel ? (
                <Button size="sm" variant="outline" colorScheme="red" onClick={onCancel}>
                  <Trans>Cancel Task</Trans>
                </Button>
              ) : null}
            </HStack>
          </VStack>
        </DrawerBody>
      </DrawerContent>

      {isReenrolmentReview && taskId != null && taskId !== '' ? (
        <>
          <ApproveReenrolmentModal
            isOpen={isApproveModalOpen}
            onClose={() => setIsApproveModalOpen(false)}
            taskId={taskId}
            blockingEnrolmentId={task.reenrolmentDetails?.currentEnrolment?.id ?? null}
            blockingEnrolmentStatus={task.reenrolmentDetails?.currentEnrolment?.status ?? null}
            proposedEnrolmentId={task.reenrolmentDetails?.proposedEnrolment?.id ?? null}
            blockingCourseName={
              formatCourseLabel(task.reenrolmentDetails?.currentEnrolment?.course ?? null) ?? null
            }
            proposedCourseName={
              formatCourseLabel(task.reenrolmentDetails?.proposedEnrolment?.course ?? null) ?? null
            }
          />
          <RejectReenrolmentModal
            isOpen={isRejectModalOpen}
            onClose={() => setIsRejectModalOpen(false)}
            taskId={taskId}
            proposedEnrolmentId={task.reenrolmentDetails?.proposedEnrolment?.id ?? null}
            proposedCourseName={
              formatCourseLabel(task.reenrolmentDetails?.proposedEnrolment?.course ?? null) ?? null
            }
          />
        </>
      ) : null}
      {isSupersededTransfer && taskId != null && taskId !== '' ? (
        <TransferSupersededEnrolmentModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          taskId={taskId}
          currentCourseName={formatCourseLabel(task.enrolment?.course ?? null) ?? null}
          supersedingCourseName={
            formatCourseLabel(task.supersessionDetails?.supersedingCourse ?? null) ?? null
          }
        />
      ) : null}
    </Drawer>
  );
}

// -- Reenrolment comparison table ------------------------------

type ReenrolmentEnrolmentDetail = {
  id: number;
  status: string;
  startDate: Date | null;
  course: { id: string; title: string; identifier: string | null } | null;
  provider: { id: number; name: string } | null;
  consultant: { drn: string; first_name: string; last_name: string | null } | null;
};

function formatCourseLabel(
  course: { title: string; identifier: string | null } | null,
): string | undefined {
  return course == null ? undefined : courseLabel(course.identifier, course.title);
}

type CreditTransferDetailRow = {
  unitCode: string;
  sourceEnrolmentId: number;
  sourceCourse: { id: string; title: string; identifier: string | null } | null;
  completedOn: Date | null;
};

function CreditTransferDetailsTable({
  details,
  enrolment,
  variant,
}: {
  details: { creditedOn: Date | null; rows: CreditTransferDetailRow[] };
  enrolment: { id: number; courseIdentifier: string | null } | null;
  variant: 'review' | 'followUp';
}) {
  const enrolmentLink =
    enrolment != null ? (
      <ChakraLink
        as={Link}
        to={`/enrolment/${enrolment.id}`}
        color="blue.500"
        textDecoration="underline"
      >
        {enrolment.courseIdentifier != null
          ? `${enrolment.courseIdentifier} · Enrolment ${enrolment.id}`
          : t`Enrolment ${enrolment.id}`}
      </ChakraLink>
    ) : (
      <Trans>this enrolment</Trans>
    );
  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
        <Trans>Credit Transfer Details</Trans>
      </Text>
      <Text fontSize="sm" mb={2}>
        {variant === 'followUp' ? (
          <Trans>
            This student is part way through units in {enrolmentLink} that they have already
            completed in another enrolment. Contact them before applying the credit transfers
            manually:
          </Trans>
        ) : details.creditedOn != null ? (
          <Trans>
            Automatically credit-transferred in {enrolmentLink} on{' '}
            {djs(details.creditedOn).tz(SYDNEY_TZ).format('DD MMM YYYY')} because this student had
            already completed these units:
          </Trans>
        ) : (
          <Trans>
            Automatically credit-transferred in {enrolmentLink} because this student had already
            completed these units:
          </Trans>
        )}
      </Text>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th fontSize="xs" textTransform="none" fontWeight="semibold" pl={0}>
              <Trans>Unit</Trans>
            </Th>
            <Th fontSize="xs" textTransform="none" fontWeight="semibold">
              <Trans>Completed in</Trans>
            </Th>
            <Th fontSize="xs" textTransform="none" fontWeight="semibold">
              <Trans>Completed on</Trans>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {details.rows.map((row) => (
            <Tr key={`${row.unitCode}-${row.sourceEnrolmentId}`}>
              <Td fontSize="sm" pl={0} fontWeight="medium">
                {row.unitCode}
              </Td>
              <Td fontSize="sm">
                <ChakraLink
                  as={Link}
                  to={`/enrolment/${row.sourceEnrolmentId}`}
                  color="blue.500"
                  textDecoration="underline"
                >
                  {row.sourceCourse?.identifier != null
                    ? `${row.sourceCourse.identifier} · Enrolment ${row.sourceEnrolmentId}`
                    : t`Enrolment ${row.sourceEnrolmentId}`}
                </ChakraLink>
              </Td>
              <Td fontSize="sm">
                {row.completedOn != null
                  ? djs(row.completedOn).tz(SYDNEY_TZ).format('DD MMM YYYY')
                  : '-'}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function ReenrolmentComparisonTable({
  current,
  proposed,
}: {
  current: ReenrolmentEnrolmentDetail | null;
  proposed: ReenrolmentEnrolmentDetail | null;
}) {
  const rows: {
    key: string;
    label: React.ReactNode;
    currentValue: React.ReactNode;
    proposedValue: React.ReactNode;
  }[] = [
    {
      key: 'course',
      label: <Trans>Course</Trans>,
      currentValue: formatCourseLabel(current?.course ?? null) ?? '-',
      proposedValue: formatCourseLabel(proposed?.course ?? null) ?? '-',
    },
    {
      key: 'provider',
      label: <Trans>Provider</Trans>,
      currentValue: current?.provider?.name ?? '-',
      proposedValue: proposed?.provider?.name ?? '-',
    },
    {
      key: 'ec',
      label: <Trans>EC</Trans>,
      currentValue:
        current?.consultant != null ? (getUserFullName(current.consultant) ?? '-') : '-',
      proposedValue:
        proposed?.consultant != null ? (getUserFullName(proposed.consultant) ?? '-') : '-',
    },
    {
      key: 'startDate',
      label: <Trans>Start date</Trans>,
      currentValue: current?.startDate != null ? djs(current.startDate).format('DD MMM YYYY') : '-',
      proposedValue:
        proposed?.startDate != null ? djs(proposed.startDate).format('DD MMM YYYY') : '-',
    },
    {
      key: 'status',
      label: <Trans>Status</Trans>,
      currentValue:
        current != null && isResourceStatus(current.status) ? (
          <ResourceStatusBadge status={current.status} />
        ) : (
          '-'
        ),
      proposedValue:
        proposed != null && isResourceStatus(proposed.status) ? (
          <ResourceStatusBadge status={proposed.status} />
        ) : (
          '-'
        ),
    },
  ];

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
        <Trans>Enrolment Comparison</Trans>
      </Text>
      <Table size="sm" variant="simple" sx={{ tableLayout: 'fixed' }}>
        <Thead>
          <Tr>
            <Th w="20%" />
            <Th fontSize="xs" textTransform="none" fontWeight="semibold" w="40%">
              <Trans>Existing</Trans>
            </Th>
            <Th fontSize="xs" textTransform="none" fontWeight="semibold" w="40%">
              <Trans>Requested</Trans>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.key}>
              <Td fontSize="sm" color="fg.muted" fontWeight="medium" pl={0} whiteSpace="nowrap">
                {row.label}
              </Td>
              <Td fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                {row.currentValue}
              </Td>
              <Td fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                {row.proposedValue}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
